<script>
import { mapGetters } from 'vuex';
import { useLocale } from 'shared/composables/useLocale';
import { useReportMetrics } from 'dashboard/composables/useReportMetrics';
import { GROUP_BY_FILTER, METRIC_CHART, formatTime } from './constants';
import fromUnixTime from 'date-fns/fromUnixTime';
import ChartStats from './components/ChartElements/ChartStats.vue';
import BarChart from 'shared/components/charts/BarChart.vue';

export default {
  components: { ChartStats, BarChart },
  props: {
    groupBy: {
      type: Object,
      default: () => ({}),
    },
    accountSummaryKey: {
      type: String,
      default: 'getAccountSummary',
    },
    summaryFetchingKey: {
      type: String,
      default: 'getAccountSummaryFetchingStatus',
    },
    reportKeys: {
      type: Object,
      default: () => ({
        CONVERSATIONS: 'conversations_count',
        INCOMING_MESSAGES: 'incoming_messages_count',
        OUTGOING_MESSAGES: 'outgoing_messages_count',
        FIRST_RESPONSE_TIME: 'avg_first_response_time',
        RESOLUTION_TIME: 'avg_resolution_time',
        RESOLUTION_COUNT: 'resolutions_count',
        REPLY_TIME: 'reply_time',
      }),
    },
  },
  setup(props) {
    const { calculateTrend, isAverageMetricType } = useReportMetrics(
      props.accountSummaryKey
    );
    const { resolvedLocale } = useLocale();
    return { calculateTrend, isAverageMetricType, resolvedLocale };
  },
  computed: {
    ...mapGetters({
      accountReport: 'getAccountReports',
    }),
    metrics() {
      const reportKeys = Object.keys(this.reportKeys);
      const infoText = {
        FIRST_RESPONSE_TIME: this.$t(
          `REPORT.METRICS.FIRST_RESPONSE_TIME.INFO_TEXT`
        ),
        RESOLUTION_TIME: this.$t(`REPORT.METRICS.RESOLUTION_TIME.INFO_TEXT`),
      };
      return reportKeys.map(key => ({
        NAME: this.$t(`REPORT.METRICS.${key}.NAME`),
        KEY: this.reportKeys[key],
        DESC: this.$t(`REPORT.METRICS.${key}.DESC`),
        INFO_TEXT: infoText[key],
        TOOLTIP_TEXT: `REPORT.METRICS.${key}.TOOLTIP_TEXT`,
        trend: this.calculateTrend(this.reportKeys[key]),
      }));
    },
  },
  methods: {
    formatDateLabel(date, options) {
      return new Intl.DateTimeFormat(this.resolvedLocale, options).format(date);
    },
    formatReportXAxisLabel(date) {
      return this.formatDateLabel(date, {
        day: 'numeric',
        month: 'short',
      });
    },
    formatReportMonthLabel(date) {
      return this.formatDateLabel(date, {
        month: 'short',
        year: 'numeric',
      });
    },
    formatReportWeekLabel(timestamp) {
      const weekDate = new Date(fromUnixTime(timestamp));
      const firstDay = weekDate.getDate() - weekDate.getDay();
      const weekFirstDate = new Date(weekDate);
      weekFirstDate.setDate(firstDay);
      const weekLastDate = new Date(weekFirstDate);
      weekLastDate.setDate(weekFirstDate.getDate() + 6);

      return `${this.formatReportXAxisLabel(weekFirstDate)} - ${this.formatReportXAxisLabel(weekLastDate)}`;
    },
    getTimeAxisTickLabel(value, index, values) {
      if (!index || index === values.length - 1) {
        return formatTime(value, this.resolvedLocale);
      }

      return '';
    },
    getCollection(metric) {
      if (!this.accountReport.data[metric.KEY]) {
        return {};
      }
      const data = this.accountReport.data[metric.KEY];
      const labels = data.map(element => {
        if (this.groupBy?.period === GROUP_BY_FILTER[2].period) {
          return this.formatReportWeekLabel(element.timestamp);
        }
        if (this.groupBy?.period === GROUP_BY_FILTER[3].period) {
          return this.formatReportMonthLabel(fromUnixTime(element.timestamp));
        }
        if (this.groupBy?.period === GROUP_BY_FILTER[4].period) {
          return this.formatDateLabel(fromUnixTime(element.timestamp), {
            year: 'numeric',
          });
        }
        return this.formatReportXAxisLabel(fromUnixTime(element.timestamp));
      });
      const datasets = METRIC_CHART[metric.KEY].datasets.map(dataset => {
        switch (dataset.type) {
          case 'bar':
            return {
              ...dataset,
              yAxisID: 'y',
              label: metric.NAME,
              data: data.map(element => element.value),
            };
          case 'line':
            return {
              ...dataset,
              yAxisID: 'y',
              label: this.metrics[0].NAME,
              data: data.map(element => element.count),
            };
          default:
            return dataset;
        }
      });
      return {
        labels,
        datasets,
      };
    },
    getChartOptions(metric) {
      const options = {
        scales: {
          ...METRIC_CHART[metric.KEY].scales,
          x: {
            ...METRIC_CHART[metric.KEY].scales?.x,
            ticks: {
              ...METRIC_CHART[metric.KEY].scales?.x?.ticks,
            },
          },
          y: {
            ...METRIC_CHART[metric.KEY].scales?.y,
            ticks: {
              ...METRIC_CHART[metric.KEY].scales?.y?.ticks,
            },
          },
        },
      };

      // Only add tooltip configuration for time-based metrics
      if (this.isAverageMetricType(metric.KEY)) {
        options.scales.y.ticks.callback = (value, index, values) =>
          this.getTimeAxisTickLabel(value, index, values);
        options.plugins = {
          tooltip: {
            callbacks: {
              label: ({ raw, dataIndex }) => {
                return this.$t(metric.TOOLTIP_TEXT, {
                  metricValue: formatTime(raw || 0, this.resolvedLocale),
                  conversationCount:
                    this.accountReport.data[metric.KEY][dataIndex]?.count || 0,
                });
              },
            },
          },
        };
      }

      return options;
    },
  },
};
</script>

<template>
  <div
    class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <div
      v-for="metric in metrics"
      :key="metric.KEY"
      class="p-4 mb-3 rounded-md"
    >
      <ChartStats
        :metric="metric"
        :account-summary-key="accountSummaryKey"
        :summary-fetching-key="summaryFetchingKey"
      />
      <div class="mt-4 h-72">
        <woot-loading-state
          v-if="accountReport.isFetching[metric.KEY]"
          class="text-xs"
          :message="$t('REPORT.LOADING_CHART')"
        />
        <div v-else class="flex items-center justify-center h-72">
          <BarChart
            v-if="accountReport.data[metric.KEY].length"
            :collection="getCollection(metric)"
            :chart-options="getChartOptions(metric)"
          />
          <span v-else class="text-sm text-n-slate-10">
            {{ $t('REPORT.NO_ENOUGH_DATA') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
